import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import API from "../../config/config";

const COOLDOWN_TIME = 60; // in seconds

const VerifyOTP = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const phoneNumber = location.state?.phone || "";
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [resendDisabled, setResendDisabled] = useState(false);
  const [timer, setTimer] = useState(0); // for countdown display

  useEffect(() => {
    let interval;
    if (resendDisabled && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setResendDisabled(false);
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendDisabled, timer]);

  const handleChange = (element, index) => {
    if (isNaN(element.value)) return;

    const newOtp = [...otp];
    newOtp[index] = element.value;
    setOtp(newOtp);

    if (element.nextSibling && element.value !== "") {
      element.nextSibling.focus();
    }
  };

  const handleVerify = async () => {
    const enteredOtp = otp.join("");
    if (enteredOtp.length !== 6) return;

    try {
      setLoading(true);
      setError("");

      const response = await axios.post(API.ADMIN.VERIFYOTP, {
        phone: phoneNumber,
        otp: enteredOtp,
        role: "driver",
        referralCode: referralCode,
      });

      const { token, nextStep } = response.data;
      localStorage.setItem("userPublicToken", token);

      if (nextStep === "completeProfile") {
        navigate("/user-dashboard");
      } else {
        setError("Unexpected response");
      }
    } catch (err) {
      console.error(err);
      setError("OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      const response = await axios.post(API.ADMIN.RESENDOTP, {
        phone: phoneNumber,
        role: "driver",
      });

      // ✅ Only start timer if resend was successful
      setResendDisabled(true);
      setTimer(COOLDOWN_TIME);

      toast.success("OTP resent successfully.", {
        position: "bottom-center",
        autoClose: 3000,
      });
    } catch (error) {
      // ❌ Do NOT start timer on error
      setResendDisabled(false);
      setTimer(0);

      toast.error(error?.response?.data?.message || "Failed to resend OTP", {
        position: "bottom-center",
        autoClose: 5000,
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4">
      <ToastContainer />
      <div className="w-full max-w-md">
        <h2 className="text-xl font-semibold text-center mb-4">Verify OTP</h2>
        <p className="text-center text-gray-700 mb-6">
          Enter the OTP sent to{" "}
          <span className="font-semibold">{phoneNumber}</span>
        </p>

        <div className="flex justify-center gap-2 mb-4">
          {otp.map((digit, index) => (
            <input
              key={index}
              type="text"
              maxLength="1"
              value={digit}
              onChange={(e) => handleChange(e.target, index)}
              className="w-12 h-12 border border-black rounded text-center text-xl focus:outline-none"
            />
          ))}
        </div>

        <div className="text-center mb-6">
          <button
            onClick={handleResendOTP}
            className={`text-blue-600 text-sm hover:underline ${
              resendDisabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
            disabled={resendDisabled}
          >
            {resendDisabled ? `Resend OTP in ${timer}s...` : "Resend OTP?"}
          </button>
        </div>

        <p className="text-center text-xs text-gray-500 mb-4">
          By clicking “Verify” you agree to our{" "}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            Terms and Conditions
          </a>{" "}
          and{" "}
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            Privacy Policy
          </a>
          .
        </p>

        {location.state?.isNewUser && (
          <div className="flex flex-col items-center mb-6">
            <label className="text-sm font-medium text-gray-700 mb-1">
              Referral Code
            </label>
            <input
              type="text"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              maxLength={9}
              className="w-[20.5rem] h-12 border border-black rounded text-center text-lg uppercase focus:outline-none"
            />
          </div>
        )}

        <button
          onClick={handleVerify}
          disabled={otp.includes("") || loading}
          className={`w-full py-3 rounded-xl font-semibold shadow-md transition ${
            otp.includes("") || loading
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-black text-white hover:opacity-90"
          }`}
        >
          {loading ? "Verifying..." : "Verify"}
        </button>

        {error && <p className="mt-4 text-center text-red-600">{error}</p>}
      </div>
    </div>
  );
};

export default VerifyOTP;
